import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { apiClient } from '@/api/client';

const googleProvider = new GoogleAuthProvider();

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  plan?: 'free' | 'starter' | 'professional' | 'business' | 'enterprise';
  apiKeys?: string[];
  createdAt?: Date;
  updatedAt?: Date;
  // Backend sync fields
  backendId?: number;
  characters_used_this_month?: number;
  requests_this_month?: number;
  total_characters?: number;
  total_requests?: number;
}

/**
 * Sync Firebase user with backend database
 * Called after successful Firebase authentication
 */
async function syncWithBackend(): Promise<UserProfile | null> {
  try {
    const response = await apiClient.verifyAndSyncUser();
    if (response.status === 'verified' && response.user) {
      return {
        uid: response.user.firebase_uid,
        email: response.user.email,
        displayName: response.user.display_name,
        plan: response.user.plan as UserProfile['plan'],
        backendId: response.user.id,
        characters_used_this_month: response.user.characters_used_this_month,
        requests_this_month: response.user.requests_this_month,
      };
    }
  } catch (error) {
    console.warn('Failed to sync with backend:', error);
    // Don't fail auth if backend sync fails - user can still use Firebase features
  }
  return null;
}

export const authService = {
  // Sign up with email and password
  async signUp(email: string, password: string, displayName?: string): Promise<User> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update profile if displayName provided
    if (displayName) {
      await updateProfile(user, { displayName });
    }

    // Create user profile in Firestore
    const userProfile: UserProfile = {
      uid: user.uid,
      email: user.email || '',
      displayName: displayName || user.displayName || '',
      plan: 'free',
      apiKeys: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    await setDoc(doc(db, 'users', user.uid), userProfile);

    // Sync with backend database
    await syncWithBackend();

    return user;
  },

  // Sign in with email and password
  async signIn(email: string, password: string): Promise<User> {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Sync with backend database
    await syncWithBackend();
    
    return userCredential.user;
  },

  // Sign out
  async signOut(): Promise<void> {
    await signOut(auth);
  },

  // Get current user
  getCurrentUser(): User | null {
    return auth.currentUser;
  },

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
  },

  // Get user profile from Firestore
  async getUserProfile(uid: string): Promise<UserProfile | null> {
    const docRef = doc(db, 'users', uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as UserProfile;
    }
    return null;
  },

  // Update user profile
  async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    const docRef = doc(db, 'users', uid);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date(),
    });
  },

  // Reset password
  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email);
  },

  // Sign in with Google
  async signInWithGoogle(): Promise<User> {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Check if user profile exists, if not create one
    const docRef = doc(db, 'users', user.uid);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      const userProfile: UserProfile = {
        uid: user.uid,
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        plan: 'free',
        apiKeys: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(docRef, userProfile);
    } else {
      // Update existing profile with Google info if needed
      const existingProfile = docSnap.data() as UserProfile;
      await updateDoc(docRef, {
        displayName: user.displayName || existingProfile.displayName,
        photoURL: user.photoURL || existingProfile.photoURL,
        updatedAt: new Date(),
      });
    }

    // Sync with backend database
    await syncWithBackend();

    return user;
  },

  // Sync current user with backend (can be called anytime)
  async syncCurrentUserWithBackend(): Promise<UserProfile | null> {
    return syncWithBackend();
  },

  // Get backend user profile
  async getBackendUserProfile(): Promise<UserProfile | null> {
    try {
      const user = await apiClient.getCurrentUser();
      return {
        uid: user.firebase_uid,
        email: user.email,
        displayName: user.display_name,
        plan: user.plan as UserProfile['plan'],
        backendId: user.id,
        characters_used_this_month: user.characters_used_this_month,
        requests_this_month: user.requests_this_month,
        total_characters: user.total_characters,
        total_requests: user.total_requests,
      };
    } catch (error) {
      console.warn('Failed to get backend user profile:', error);
      return null;
    }
  },
};

